import { cva } from 'class-variance-authority';

export const alertVariants = cva(
  'relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[1rem_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        destructive:
          'text-destructive bg-card [&>svg]:text-current [&_[data-slot=alert-description]]:text-destructive/90',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        muted: 'border-transparent bg-muted text-muted-foreground',
        outline: 'text-foreground',
        destructive: 'border-transparent bg-destructive text-white',
        success: 'border-transparent bg-success text-success-foreground',
        positive: 'border-success/30 bg-success/15 text-success',
        warning: 'border-transparent bg-warning text-warning-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20',
        outline:
          'border bg-background text-foreground shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-xs': 'size-6 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export const dialogContentVariants = cva(
  'fixed z-50 border bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
  {
    variants: {
      variant: {
        centered:
          'top-1/2 left-1/2 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 p-6 sm:rounded-lg data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        responsive:
          'flex max-h-[92svh] flex-col gap-4 p-6 max-md:inset-x-0 max-md:bottom-0 max-md:w-full max-md:rounded-t-lg max-md:pb-[calc(1.5rem+env(safe-area-inset-bottom))] max-md:data-[state=closed]:slide-out-to-bottom max-md:data-[state=open]:slide-in-from-bottom md:top-1/2 md:left-1/2 md:max-h-[85svh] md:w-full md:max-w-lg md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-lg md:data-[state=closed]:zoom-out-95 md:data-[state=open]:zoom-in-95',
      },
      mobileKeyboardSafe: {
        true: 'max-md:bottom-[var(--visual-viewport-bottom)] max-md:max-h-[calc(var(--visual-viewport-height)*0.92)]',
        false: null,
      },
    },
    defaultVariants: { variant: 'centered', mobileKeyboardSafe: false },
  },
);

export const emptyMediaVariants = cva(
  'mb-2 flex shrink-0 items-center justify-center [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        icon: "flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground [&_svg:not([class*='size-'])]:size-4",
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export const toggleGroupVariants = cva('inline-flex w-fit items-center', {
  variants: {
    variant: {
      outline: 'overflow-hidden rounded-md border border-input',
      pill: 'h-9 rounded-lg bg-muted p-1 text-muted-foreground',
    },
  },
  defaultVariants: { variant: 'outline' },
});

export const toggleGroupItemVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap font-medium text-sm outline-none transition-all disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        outline:
          'h-8 border-input border-l px-3 transition-colors first:border-l-0 hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent data-[state=on]:bg-primary data-[state=on]:text-primary-foreground',
        pill: 'h-7 rounded-md px-3 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow',
      },
    },
    defaultVariants: { variant: 'outline' },
  },
);

export const dropdownMenuSubTriggerVariants = cva(
  'flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent data-[state=open]:bg-accent',
  {
    variants: { inset: { true: 'pl-8', false: null } },
    defaultVariants: { inset: false },
  },
);

export const dropdownMenuItemVariants = cva(
  'relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: { inset: { true: 'pl-8', false: null } },
    defaultVariants: { inset: false },
  },
);

export const dropdownMenuLabelVariants = cva('px-2 py-1.5 text-sm font-semibold', {
  variants: { inset: { true: 'pl-8', false: null } },
  defaultVariants: { inset: false },
});
